import { Navigate, Outlet } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../context/UserContext';
import Layout from '../components/templates/Layout/Layout';
import { LoadingSpinner } from '@rabbit/elements/shared-components';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { FileStorageProviderWrapper } from '@rabbit/bizproc/react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { PersonaTypeSingleLetter } from '@rabbit/data/types';
import AccessDeniedView from '../views/AccessDeniedView';

const PrivateRoutes = (props: any) => {
  const contextValues = useContext(UserContext);
  const { tenantInfo } = useContext(AppContext);
  const { t } = useTranslation();

  const [cancelable, setCancelable] = useState(false);
  useEffect(function () {
    const timeout = setTimeout(function () {
      setCancelable(true);
    }, 7200);

    return function () {
      clearTimeout(timeout);
    };
  }, []);

  const isPlatformAdmin = useMemo(() => {
    const email =
      (contextValues?.user?.email ??
        (contextValues?.accessToken as any)?.email ??
        '') as string;
    return (
      contextValues?.accessToken?.admin === true &&
      email.toLowerCase() === 'admin@iwarranty.co'
    );
  }, [contextValues?.accessToken, contextValues?.user?.email]);

  const personaRole = useMemo(() => {
    if (contextValues?.accessToken?.admin) return 'ADMIN';
    const claimKeys = contextValues?.accessToken
      ? Object.keys(contextValues.accessToken)
      : [];
    const personaKey = claimKeys.find((key) => {
      const [prefix] = key.split(':');
      return Object.values(PersonaTypeSingleLetter).includes(
        prefix as PersonaTypeSingleLetter
      );
    });
    if (!personaKey) return null;
    return personaKey.split(':')[0] as PersonaTypeSingleLetter;
  }, [contextValues?.accessToken]);

  const isAccessDenied = useMemo(() => {
    if (isPlatformAdmin) return false;
    if (!tenantInfo) return false;
    const email = contextValues?.user?.email?.toLowerCase();
    const emailBlocked = email
      ? tenantInfo.access_deny_emails?.some(
          (entry) => entry?.toLowerCase() === email
        )
      : false;

    if (tenantInfo.access_deny_all) return true;

    switch (personaRole) {
      case 'ADMIN':
        return !!tenantInfo.access_deny_admin || emailBlocked;
      case PersonaTypeSingleLetter.Repairer:
        return !!tenantInfo.access_deny_repairer || emailBlocked;
      case PersonaTypeSingleLetter.Retailer:
        return !!tenantInfo.access_deny_retailer || emailBlocked;
      case PersonaTypeSingleLetter.Manufacturer:
        return !!tenantInfo.access_deny_manufacturer || emailBlocked;
      case PersonaTypeSingleLetter.Warrantor:
        return !!tenantInfo.access_deny_warrantor || emailBlocked;
      default:
        return emailBlocked;
    }
  }, [contextValues?.user?.email, personaRole, tenantInfo]);

  if (contextValues && !contextValues.user)
    return (
      <LoadingSpinner
        size="lg"
        isFullScreen
        label="Loading..."
        action={{
          actionLabel: 'Stuck? Click here to return to Login.',
          actionFn: () => {
            contextValues?.handleSignout();
          },
        }}
      />
    );

  if (contextValues !== null && contextValues.user === null) {
    if (window.location.pathname !== '/login')
      localStorage.setItem('redirectUrl', window.location.pathname);
    return <Navigate to={SAGE_ROUTE_NAME.LOGIN} />;
  }

  if (
    !contextValues?.user ||
    !contextValues?.accessToken ||
    (!contextValues?.accessToken?.admin &&
      !contextValues?.accessToken?.T?.includes(t('tenantLink')))
  ) {
    if (cancelable) {
      return (
        <LoadingSpinner
          size="lg"
          isFullScreen
          label="Loading..."
          action={{
            actionLabel: 'Stuck? Click here to return to Login.',
            actionFn: () => {
              contextValues?.handleSignout();
            },
          }}
        />
      );
    }
    return <LoadingSpinner size="lg" isFullScreen label="Loading..." />;
  }

  if (isAccessDenied) {
    return <AccessDeniedView />;
  }

  if (location.pathname.includes('/print')) return <Outlet />;

  return (
    <FileStorageProviderWrapper>
      <Layout>
        <Outlet />
      </Layout>
    </FileStorageProviderWrapper>
  );
};

export default PrivateRoutes;
